// src/pages/Contact.jsx - AVEC TRADUCTION
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import emailjs from '@emailjs/browser';
import { EMAILJS_CONFIG } from '../config/emailConfig';
import { useTranslation } from 'react-i18next';
import { Phone, Mail, MapPin, Send, User, MessageCircle, CheckCircle, AlertCircle } from 'lucide-react';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState({ type: '', message: '' });
  const { t } = useTranslation();

  const contactInfo = {
    phone: "+46 76 565 95 51",
    email: "esaiemusic@gmail.com",
    location: t('contact.info.location'),
    hours: t('contact.info.hours'),
    responseTime: t('contact.info.responseTime'),
    ministry: t('contact.info.ministry')
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.name.trim() || !formData.email.trim() || !formData.message.trim()) {
      setSubmitStatus({ 
        type: 'error', 
        message: t('contact.validation.requiredFields') 
      });
      return;
    }

    // Validation email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setSubmitStatus({ 
        type: 'error', 
        message: t('contact.validation.invalidEmail') 
      });
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus({ type: '', message: '' });

    try {
      // Envoyer l'email à l'admin
      const result = await emailjs.send(
        EMAILJS_CONFIG.SERVICE_ID,
        EMAILJS_CONFIG.TEMPLATE_CONTACT,
        {
          name: formData.name.trim(),
          email: formData.email.trim(),
          subject: formData.subject.trim() || t('contact.email.defaultSubject'),
          message: formData.message.trim(),
          date: new Date().toLocaleDateString('fr-FR', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric'
          }),
          time: new Date().toLocaleTimeString('fr-FR', { 
            hour: '2-digit', 
            minute: '2-digit' 
          }),
          reply_to: formData.email.trim()
        },
        EMAILJS_CONFIG.PUBLIC_KEY
      );

      console.log('Email envoyé avec succès:', result);

      // Réinitialiser le formulaire
      setFormData({
        name: '',
        email: '',
        subject: '',
        message: ''
      });

      setSubmitStatus({ 
        type: 'success', 
        message: t('contact.successMessage') 
      });

      // Effacer le message après 5 secondes
      setTimeout(() => {
        setSubmitStatus({ type: '', message: '' });
      }, 5000);

    } catch (error) {
      console.error('Erreur EmailJS:', error);
      
      let errorMessage = t('contact.error.general');
      if (error.text && error.text.includes('quota')) {
        errorMessage = t('contact.error.quota');
      }
      
      setSubmitStatus({ 
        type: 'error', 
        message: errorMessage 
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white pt-24">
      {/* Hero Section */}
      <section className="relative overflow-hidden py-20">
        <div className="absolute inset-0 bg-gradient-to-b from-black via-black/80 to-black z-10"></div>
        <div 
          className="absolute inset-0 z-0 opacity-20"
          style={{
            backgroundImage: `url('https://images.unsplash.com/photo-1513475382585-d06e58bcb0e0?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80')`,
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        ></div>
        
        <div className="container mx-auto px-4 relative z-20">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-4xl mx-auto"
          >
            <div className="inline-flex items-center gap-3 mb-6">
              <div className="w-16 h-16 bg-gradient-to-br from-yellow-600/20 to-yellow-800/20 rounded-2xl flex items-center justify-center border border-yellow-500/30">
                <MessageCircle className="w-8 h-8 text-yellow-600" />
              </div>
              <h1 className="text-5xl md:text-7xl font-bold font-serif">
                {t('contact.title.part1')}
                <span className="text-yellow-600">{t('contact.title.part2')}</span>
              </h1>
            </div>
            <p className="text-xl md:text-2xl text-gray-300 mb-8 leading-relaxed">
              {t('contact.subtitle')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-20 bg-gradient-to-b from-black to-gray-900">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Informations */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="space-y-8"
            >
              <div className="bg-gradient-to-br from-gray-900/50 to-black/50 rounded-3xl p-8 border border-gray-800">
                <h2 className="text-3xl font-bold text-white mb-8">
                  {t('contact.contactHow.part1')}
                  <span className="text-yellow-600">{t('contact.contactHow.part2')}</span>
                </h2>
                
                <div className="space-y-8">
                  <div className="flex items-start space-x-4">
                    <div className="w-14 h-14 bg-gradient-to-br from-yellow-600/20 to-yellow-800/20 rounded-xl flex items-center justify-center flex-shrink-0 border border-yellow-500/30">
                      <Phone className="w-6 h-6 text-yellow-600" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-white mb-2">
                        {t('contact.info.phoneLabel')}
                      </h3>
                      <p className="text-gray-300">{contactInfo.phone}</p>
                      <p className="text-gray-500 text-sm">{contactInfo.hours}</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="w-14 h-14 bg-gradient-to-br from-yellow-600/20 to-yellow-800/20 rounded-xl flex items-center justify-center flex-shrink-0 border border-yellow-500/30">
                      <Mail className="w-6 h-6 text-yellow-600" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-white mb-2">
                        {t('contact.info.emailLabel')}
                      </h3>
                      <p className="text-gray-300">{contactInfo.email}</p>
                      <p className="text-gray-500 text-sm">{contactInfo.responseTime}</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="w-14 h-14 bg-gradient-to-br from-yellow-600/20 to-yellow-800/20 rounded-xl flex items-center justify-center flex-shrink-0 border border-yellow-500/30">
                      <MapPin className="w-6 h-6 text-yellow-600" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-white mb-2">
                        {t('contact.info.locationLabel')}
                      </h3>
                      <p className="text-gray-300">{contactInfo.location}</p>
                      <p className="text-gray-500 text-sm">{contactInfo.ministry}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="p-6 bg-gradient-to-br from-gray-900/30 to-black/30 rounded-2xl border border-yellow-500/20">
                <h3 className="text-xl font-bold text-white mb-4">
                  {t('contact.commitment.title')}
                </h3>
                <p className="text-gray-300 leading-relaxed">
                  {t('contact.commitment.description')}
                </p>
              </div>
            </motion.div>

            {/* Formulaire */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="bg-gradient-to-b from-gray-900/50 to-black/50 rounded-3xl p-8 border border-gray-800"
            >
              <h2 className="text-3xl font-bold text-white mb-8">
                {t('contact.form.title')}
              </h2>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-gray-300 mb-2">
                      {t('contact.form.name')} *
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        className="w-full px-4 py-3 bg-gray-900 border border-gray-700 rounded-lg focus:outline-none focus:border-yellow-600 focus:ring-2 focus:ring-yellow-500/20 text-white placeholder-gray-500 transition-all duration-300 pl-12"
                        required
                        disabled={isSubmitting}
                        placeholder={t('contact.form.namePlaceholder')}
                      />
                      <div className="absolute left-4 top-3.5">
                        <User className="w-5 h-5 text-gray-500" />
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="block text-gray-300 mb-2">
                      {t('contact.form.email')} *
                    </label>
                    <div className="relative">
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        className="w-full px-4 py-3 bg-gray-900 border border-gray-700 rounded-lg focus:outline-none focus:border-yellow-600 focus:ring-2 focus:ring-yellow-500/20 text-white placeholder-gray-500 transition-all duration-300 pl-12"
                        required
                        disabled={isSubmitting}
                        placeholder={t('contact.form.emailPlaceholder')}
                      />
                      <div className="absolute left-4 top-3.5">
                        <Mail className="w-5 h-5 text-gray-500" />
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-gray-300 mb-2">
                    {t('contact.form.subject')}
                  </label>
                  <input
                    type="text"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    placeholder={t('contact.form.subjectPlaceholder')}
                    className="w-full px-4 py-3 bg-gray-900 border border-gray-700 rounded-lg focus:outline-none focus:border-yellow-600 focus:ring-2 focus:ring-yellow-500/20 text-white placeholder-gray-500 transition-all duration-300"
                    disabled={isSubmitting}
                  />
                </div>

                <div>
                  <label className="block text-gray-300 mb-2">
                    {t('contact.form.message')} *
                  </label>
                  <div className="relative">
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      rows="6"
                      className="w-full px-4 py-3 bg-gray-900 border border-gray-700 rounded-lg focus:outline-none focus:border-yellow-600 focus:ring-2 focus:ring-yellow-500/20 text-white placeholder-gray-500 transition-all duration-300 resize-none pl-12"
                      required
                      disabled={isSubmitting}
                      placeholder={t('contact.form.messagePlaceholder')}
                    ></textarea>
                    <div className="absolute left-4 top-3.5">
                      <MessageCircle className="w-5 h-5 text-gray-500" />
                    </div>
                  </div>
                </div>

                {/* Message de statut */}
                <AnimatePresence>
                  {submitStatus.message && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      className={`p-4 rounded-lg ${submitStatus.type === 'success' 
                        ? 'bg-green-900/20 border border-green-800/30 text-green-400' 
                        : 'bg-red-900/20 border border-red-800/30 text-red-400'
                      }`}
                    >
                      <div className="flex items-center space-x-2">
                        {submitStatus.type === 'success' ? (
                          <CheckCircle className="w-5 h-5" />
                        ) : (
                          <AlertCircle className="w-5 h-5" />
                        )}
                        <span className="text-sm">{submitStatus.message}</span>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`w-full bg-gradient-to-r from-yellow-600 to-yellow-700 text-white font-semibold py-4 px-8 rounded-lg transition-all duration-300 flex items-center justify-center space-x-3 ${
                    isSubmitting 
                      ? 'opacity-70 cursor-not-allowed' 
                      : 'hover:shadow-2xl hover:shadow-yellow-500/20 hover:scale-105'
                  }`}
                >
                  {isSubmitting ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                      <span>{t('contact.form.sending')}</span>
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5" />
                      <span>{t('contact.form.submit')}</span>
                    </>
                  )}
                </button>

                <p className="text-gray-500 text-sm text-center">
                  {t('contact.form.disclaimer')}
                </p>
              </form>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Verset biblique */}
      <section className="py-20 bg-black">
        <div className="container mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-2xl mx-auto"
          >
            <div className="p-8 border-l-4 border-yellow-600 bg-gradient-to-r from-gray-900/30 to-transparent">
              <p className="text-xl text-gray-300 italic leading-relaxed">
                {t('contact.bibleVerse.text')}
              </p>
              <p className="text-yellow-600 font-semibold mt-4">
                {t('contact.bibleVerse.reference')}
              </p>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Contact;